/* eslint-disable no-useless-escape */
import {
  generateNavigation,
  getDocSummary,
  getDocumentBySlug,
  getPagesByDocumentID,
  getRelatedDocuments,
  getRelationships,
} from '../../services/axios';
import store from '../index';
import pluralize from 'pluralize';
import {
  SET_DOCUMENT_LOADING,
  SET_CURRENT_DOCUMENT,
  SET_CURRENT_PAGE,
  SEARCH,
  SET_LARGE_PAGE_LOADING,
  RELATED_SECTIONS,
  RELATED_DOCUMENTS,
  CLEAR_SEARCH,
  DOC_SEARCH,
  DOC_SEARCH_LOADING,
  SEARCH_LOADING,
  CLEAR_DOC_SEARCH,
} from './documentTypes';
import {
  clearString,
  defineApplicableGlossaries,
  notification,
  pageHasContent,
} from '../../assets/constants/strings';
import { filterPages, RGTitleSearch, SearchPHP } from '../../services/search';

export const setLoading = () => ({
  type: SET_DOCUMENT_LOADING,
});

export const setLargePageLoading = () => ({
  type: SET_LARGE_PAGE_LOADING,
});

export const findFirstPageWithContent = (pages) => {
  // if a the first page of a document is empty, loop until finding a populated page
  let pageNotSet = true;
  let firstPage;

  pages.forEach((page) => {
    if (pageHasContent(page.HTMLContent) && pageNotSet === true) {
      pageNotSet = false;
      firstPage = page;
    }
  });

  // if it doesnt find any, display the first one
  if (pageNotSet === true) firstPage = pages[0];

  return firstPage;
};

/**
 * Get Document by slug or ID
 * @param {string} slug An identifier to find the document in the list
 * @returns The Document object based on the slug or ID provided
 */
const getDocument = async (slug) => {
  try {
    const documents = store.getState().client.documents;

    // if user tries to type page slug or paste the document title
    slug = slug.toLowerCase().replace(/(\s|(%20))/gi, '-');

    const document =
      documents && documents.length > 0
        ? // if the store is already populated, use metadata already present,
          documents.filter((doc) => doc.slug === slug || doc.id === slug)[0]
        : // otherwise, fetch metadata for specific document
          await getDocumentBySlug(slug);

    if (document === 'Error') return 'Error';

    let pages = store.getState().client.pages;
    if (!pages)
      pages = await getPagesByDocumentID(document.id, document.slug);

    const docPages = pages.filter((page) => page.documentId === document.id);

    document.summary = await getDocSummary(document.id);
    document.pages = docPages;
    document.navigation = generateNavigation(docPages);
    return document;
  } catch (e) {
    console.log('ERROR in getDocument', e);
  }
};

/**
 * Update the current document in the store
 * @param {string} slug The slug of the document
 * @param {string} pageSlug The slug of the page
 * @returns Updates the store with the selected document
 */
export const setCurrentDocument = (slug, pageSlug) => {
  return async (dispatch, getState) => {
    if (getState().document.loading === false) {
      dispatch(setLoading());
      if (slug !== null) {
        const document = await getDocument(slug);
        // todo investigate if this logic is working
        if (document === 'Error' || !document) {
          dispatch({
            type: SET_CURRENT_DOCUMENT,
            payload: document,
          });
          return;
        }
        let page = null;
        if (pageSlug) {
          // if user tries to type page slug
          pageSlug = pageSlug.toLowerCase().replace(' ', '-');
          // attempting to match ID or slug in case ID is used
          page = document.pages.filter(
            (p) => p.slug === pageSlug || p.id === pageSlug
          )[0];
        }

        if (!page) page = findFirstPageWithContent(document.pages);

        dispatch({
          type: SET_CURRENT_PAGE,
          payload: page,
        });
        dispatch({
          type: SET_CURRENT_DOCUMENT,
          payload: document,
        });
      } else {
        dispatch({
          type: SET_CURRENT_PAGE,
          payload: null,
        });
        dispatch({
          type: SET_CURRENT_DOCUMENT,
          payload: null,
        });
        dispatch({
          type: RELATED_DOCUMENTS,
          payload: null,
        });
        dispatch({
          type: RELATED_SECTIONS,
          payload: null,
        });
      }
    }
  };
};

export const getSectionRelationships = (page, documentPages) => {
  return async (dispatch, getState) => {
    // if (store.getState().document.loading === false) {
    // console.log('CALLLED getSectionRelationships');

    // removed set loading to show proper state on Document page
    // dispatch(setLoading());

    const response = await getRelationships(page, documentPages);

    // console.log('getSectionRelationships', response);

    dispatch({
      type: RELATED_SECTIONS,
      payload: response,
    });
    // }
  };
};

export const getDocumentRelationships = (docId) => {
  return async (dispatch, getState) => {
    // if (store.getState().document.loading === false) {
    // console.log('CALLED getDocumentRelationships');

    // removed set loading to show proper state on Document page
    // dispatch(setLoading());

    const response = await getRelatedDocuments(docId);

    // console.log('getDocumentRelationships', response);

    dispatch({
      type: RELATED_DOCUMENTS,
      payload: response,
    });
    // }
  };
};

export const previewLinks = (page) => {
  let html = page.HTMLContent;
  const sameDocLinkRegEx = new RegExp(`href="#/${page.documentId}`, 'g');
  if (html.match(sameDocLinkRegEx)) {
    html = html.replace(sameDocLinkRegEx, () => {
      return `href="#/PREVIEW/${page.documentId}`;
    });
  }
  return html;
};

export const setCurrentPage = (page, documentPages) => {
  return async (dispatch) => {
    // large page/document logic
    if (!documentPages)
      documentPages = store.getState().document.currentDocument.pages;

    if (documentPages && documentPages.length > 90)
      dispatch(setLargePageLoading());

    dispatch({
      type: SET_CURRENT_PAGE,
      payload: page,
    });
  };
};

export function applyGlossariesToPage(glossaries, html) {
  try {
    const thisPageGlossaries = defineApplicableGlossaries(glossaries);

    const div = document.createElement('div');
    div.innerHTML = html;
    const textContent = div.textContent;

    const allTerms = [];
    thisPageGlossaries.forEach((glossary) =>
      allTerms.push(
        ...glossary.Content.map((item) => ({
          ...item,
          hierarchy: glossary.hierarchy,
        }))
      )
    );

    // glossary terms are only highlighted once per page, so we need this array
    // to keep track of the terms that have already been highlighted and not do it
    // again with a different definition
    const termsHighlighted = [];

    allTerms
      .sort((a, b) => (a.Key.length < b.Key.length ? 1 : -1))
      .forEach((item) => {
        const key = clearString(item.Key);

        // simple regex just to match the plain text
        const regEx = new RegExp(`(\\b${key}\\b)`, 'i');

        // removed 'g' from regEx to replace only once
        // regex to only match key if outside of another definition or outside links and headings
        const regExHTML = new RegExp(
          `(\\b${key}\\b)(?=[^<>]*(?:<\\w|<\/[^a]|$))(?!((.|\\n)(?!<a|<h[1-6]|<strong))*(<\/(a|h[1-6]|strong)>))`,
          'i'
        );

        const match = textContent.match(regEx);

        if (
          match !== null &&
          termsHighlighted.filter(
            (highlighted) =>
              // if it matches and we have a higher hierarchy already highlighted
              highlighted.key === item.Key &&
              highlighted.hierarchy >= item.hierarchy
          ).length < 1
        ) {
          // add term to highlighted array to ensure we dont highlight it again with another definition
          termsHighlighted.push({
            key: item.Key,
            hierarchy: item.hierarchy,
          });
          if (item.Definition.length <= 275) {
            html = html.replace(regExHTML, () => {
              return `<span data-toggle="tooltip" data-html="true" title="${item.Definition.replace(
                /['’]/g,
                `&#39;` // logic to properly replace single quote
              )}"><strong>${match[0]}</strong></span>`;
            });
          } else {
            html = html.replace(regExHTML, () => {
              return `<span data-toggle="tooltip" data-html="true" id="MODAL_${key}" class="clickable-glossary" title="${item.Definition.replace(
                /['’]/g,
                `&#39;` // logic to properly replace single quote
              ).substring(0, 275)} ... (Click to Read More)"><strong>${
                match[0]
              }</strong></span>`;
            });
          }
        }
      });

    return html.replace(/\\r|\\n/g, '<br />');
  } catch (e) {
    console.log('error while applying glossaries', e);
    return html;
  }
}

export const clearSearch = () => async (dispatch) => {
  dispatch({
    type: CLEAR_SEARCH,
  });
};

export const clearDocSearch = () => async (dispatch) => {
  dispatch({
    type: CLEAR_DOC_SEARCH,
  });
};

const setDocSearchLoading = () => ({
  type: DOC_SEARCH_LOADING,
});

const setSearchLoading = () => ({
  type: SEARCH_LOADING,
});

/**
 * Search for a term on page contents or titles
 * @param {string} input The term to be searched.
 * @param {boolean} isAdvanced Determines if its a filtered search.
 * @param {Array<{ key: string; value: string | Date; type: string; active?: boolean }>} filters The filters to perform advanced search.
 * @param {boolean} substringSearch Boolean to dertermine if the term should be matched if occurrs as a substring of another term.
 * @returns No return, dispatches action with search results.
 */
export const Search = (input, isAdvanced, filters, substringSearch = false) => {
  return async (dispatch, getState) => {
    // if provided string is valid
    if (input.replace(/([^A-Z\d\s])/gi, '').length > 1) {
      //#region Doc Search
      // set application to load state
      dispatch(setDocSearchLoading());

      const clientDocs = [...getState().client.documents];

      const docs = RGTitleSearch(input, clientDocs, filters);

      dispatch({
        type: DOC_SEARCH,
        payload: docs,
      });
      //#endregion

      //#region Page Search
      // set application to load state
      dispatch(setSearchLoading());

      let pages = await SearchPHP(input, substringSearch);

      // if its an advanced search with filters
      if (isAdvanced && filters.length > 0 && pages.length > 0) {
        pages = filterPages(pages, filters);
      }

      // prevent dispatching if user exited search or another search was finished first
      if (getState().document.pageSearchLoading === false) return;

      dispatch({
        type: SEARCH,
        payload: pages,
      });
      //#endregion}
    } else {
      notification(
        'info',
        'Invalid Search',
        'Please enter at least 2 (two) alphanumeric (a-z, 0-9) characters for searching.'
      );
    }
  };
};

// export const SearchDocuments = (input, filters) => {
//   return async (dispatch, getState) => {
//     // set application to load state
//     dispatch(setDocSearchLoading());

//     const clientDocs = [...getState().client.documents];

//     const docs = RGTitleSearch(input, clientDocs, filters);

//     dispatch({
//       type: DOC_SEARCH,
//       payload: docs
//     });
//   };
// };

// export const SearchPages = (input, isAdvanced, filters, substringSearch) => {
//   return async (dispatch, getState) => {
//     // set application to load state
//     dispatch(setSearchLoading());

//     let pages = await SearchPHP(input, substringSearch);

//     // if its an advanced search with filters
//     if (isAdvanced && filters.length > 0 && pages.length > 0) {
//       pages = filterPages(pages, filters);
//     }

//     // prevent dispatching if user exited search or another search was finished first
//     if (getState().document.pageSearchLoading === false) return;

//     dispatch({
//       type: SEARCH,
//       payload: pages
//     });
//   };
// };

// making generic function to use algorithm on ComparisonTags
/**
 * The search algorithm to find terms within the specified pages array.
 * @param {string} input The term to be searched
 * @param {Array} targetPages The filtered pages to be searched
 * @param {boolean} substringSearch Boolean to dertermine if the term should be matched if occurrs as a substring of another term.
 * @returns The pages that matched with the specified search term and its filters.
 */
export async function genericInputSearchOnPages(
  input,
  targetPages,
  substringSearch
) {
  try {
    const keywordMatchingPages = [];

    const text = clearString(input.trim());
    const inputSingular = pluralize.singular(text);
    const inputPlural = pluralize(text);

    const div = document.createElement('div');

    targetPages.forEach((page) => {
      const newPage = { ...page };

      div.innerHTML = newPage.HTMLContent;
      newPage.textContent = div.textContent;

      const regExSingular = new RegExp(
        substringSearch ? `(${inputSingular})` : `(\\b${inputSingular}\\b)`,
        'gi'
      );
      const regExPlural = new RegExp(
        substringSearch ? `(${inputPlural})` : `(\\b${inputPlural}\\b)`,
        'gi'
      );

      // (>(.*?)[span](.*?)<)

      // if it matches the plural word in the written text
      if (newPage.textContent.match(regExPlural)) {
        const regExHTML = new RegExp(
          substringSearch
            ? `((?![^<]*>)${inputPlural})`
            : `((?![^<]*>)\\b${inputPlural}\\b`,
          'gi'
        );

        newPage.HTMLContent = newPage.HTMLContent.replace(regExHTML, () => {
          return '<mark>' + inputPlural + '</mark>';
        });
      }
      // if it matches the singular word in the written text
      if (newPage.textContent.match(regExSingular)) {
        const regExHTML = new RegExp(
          substringSearch
            ? `((?![<])${inputSingular}(?![>]))`
            : `((?![<])\\b${inputSingular}\\b(?![>]))`,
          'gi'
        );

        newPage.HTMLContent = newPage.HTMLContent.replace(regExHTML, () => {
          return '<mark>' + inputSingular + '</mark>';
        });
      }
      // splitted the array.push method to avoid pushing it twice in case we had plural and singular on same page
      if (
        newPage.textContent.match(regExSingular) ||
        newPage.textContent.match(regExPlural) ||
        newPage.title.match(regExSingular) ||
        newPage.title.match(regExPlural)
      ) {
        keywordMatchingPages.push(newPage);
      }
    });
    return keywordMatchingPages;
  } catch (e) {
    return [];
  }
}

// console testing
/* //const inputSingular = 'five years';
//const regExSingular = new RegExp(`(\\b${inputSingular}\\b)`, 'gi');
let str = 'five years is a very long time. My dog is five years old';
str.match(regExSingular).forEach((match, i) => {
    console.log(match, i);

    match.split(' ').forEach((word) => {
        str = str.replace(word, () => {
        return '<mark>' + word + '</mark>';
      })
    })
})
console.log(str) */
